import { config } from "./config.js";
import { createWorld, step, xpToNext } from "./sim/world.js";
import { createRecorder } from "./sim/replay.js";
import { WEAPONS, maxShield, attackPower } from "./sim/upgrades.js";
import { describeChoice } from "./ui/labels.js";
import { radarSvg, RADAR_ICON } from "./ui/radar.js";
import { choiceIcon } from "./ui/icons.js";
import { t, has, applyDom, setLang, getLang, getLangChoice, onLangChange } from "./i18n/index.js";
import { createView } from "./view/index.js";
import { planFinale, finaleView } from "./view/finale.js";
import { REPLAY_CAMERA_MODES, replayCameraName } from "./view/replay-camera.js";
import { createInput } from "./input.js";
import { createSound } from "./sound.js";
import { createPowerSave } from "./power-save.js";
import { loadSave, writeSave, unlockedMissions } from "./meta/save.js";
import { isTestMode } from "./meta/test-mode.js";
import { getDevice, setDevice } from "./meta/device.js";
import { mission, missionInfo, MISSION_COUNT } from "./sim/missions.js";
import { bonusesFor } from "./meta/hangar.js";
import { settleRun } from "./meta/settle.js";
import { renderHangar, renderSettlement, renderStageCards } from "./meta/screens.js";
import { createStats } from "./ui/stats.js";
import { setupInstall } from "./ui/install.js";
import { composeSnapshot, deliverSnapshot, snapshotName } from "./ui/snapshot.js";

// 画面の流れ(タイトル → 試合 → 撃墜/クリア → 結果)と、試合を一定の間隔(毎秒60ステップ)で進める部分。
// 試合の計算は src/sim/、描画は src/view/。ここは両者をつなぐだけ。
// 画面に出す文は src/i18n/ の表から t("キー") で取り出す(日本語・英語)。

const $ = (id) => document.getElementById(id);
const canvas = $("game");
const view = createView(canvas);
view.warmUp(); // 大きな敵の形を先に GPU に載せる(初めて母艦が映る瞬間の止まりを防ぐ)
const input = createInput(canvas);
const sound = createSound();
const TICK = 1 / config.sim.tickRate;

let mode = "title"; // title | play | tutorial | levelup | pause | chart | dead | finale | mission-clear | replay | result
let world = null;
let rec = null;
let stock = 0;
let pendingChoice = null;
let acc = 0;
let replay = null; // { world, playing, speed, fromDeath }
// 手ごたえ(見た目と画面の止まり方だけ。試合の計算には入れない)
let hitStop = 0; // 残りのヒットストップ(実時間の秒)
let lastStopAt = -1;
const combo = { n: 0, lastAt: -99 }; // lastAt は試合の中の時間(world.time)
// タイトルの後ろで流す見本の試合(記録しない)
let demo = createWorld({ seed: 12345 });
// ずっと続く記録(DOPA・格納庫・目標。src/meta/)と、この試合のまとめに使う数
let save = loadSave();
let run = null; // { maxCombo, bossKilled, rewinds, settled }
// R.W.N.D.の救済: 位置選びの画面で選んだもの(rescuePick)と、再開の最初のステップに入れるもの(pendingRescue)
let rescuePick = { heal: false, power: false };
let pendingRescue = null;
// クリアの後の大爆発(src/view/finale.js。見た目だけ)。lastBigKill: 最後に倒した大物の「倒した」出来事と、その試合の中の時刻
let finale = null; // { t, seconds, events, gone }
let lastBigKill = null;

$("sister-link").href = $("result-sister-link").href = config.links.senseofvoid;
// タイトルの「ホーム画面に追加」(スマホ・タブレットだけ。src/ui/install.js)
setupInstall({ button: $("install-btn"), help: $("install-help"), helpText: $("install-help-text"), closeButton: $("install-close"), t });
// 版の番号(公開するときに tools/build-site.mjs が meta に書く。手元では dev)。不具合を知らせてもらうときに見てもらう
// 全解放モード(プレビュー・?all。src/meta/test-mode.js)のときは、そうと分かるよう印を付ける
$("title-version").textContent = (document.querySelector('meta[name="app-version"]')?.content || "dev") + (isTestMode() ? " · TEST (ALL UNLOCKED)" : "");

// ---- オペレーター(ミラ)の一言と、遊び方の説明 ----
// 2026-10-31 オーナー要望「ミラのメッセージが早くて読めない。短文にして画面上部に少し大きく。ミラ以外の説明とは枠を分けて」。
// ミラは短い一言だけ(画面の上)。遊び方の説明は hint(画面の下の別の枠)。
// 次の一言が来ても、今の一言を config.operator.minSeconds は見せてから替える(待ちは config.operator.queueMax 個まで。あふれたら古いものを捨てる)
const timeCalls = new Set(); // 「残りn分！」を言った分(announceTimeLeft)
let opTimer = 0;
let opShown = 0; // 今の一言を見せてからの秒
const opQueue = [];
function say(text, face = "default", seconds = config.operator.seconds) {
  if (opTimer > 0 && opShown < config.operator.minSeconds) {
    opQueue.push({ text, face, seconds });
    if (opQueue.length > config.operator.queueMax) opQueue.shift();
    return;
  }
  showOperator({ text, face, seconds });
}
function showOperator({ text, face, seconds }) {
  $("operator-text").textContent = text;
  $("operator-face").src = `assets/textures/mila-${face}.webp`;
  $("operator").classList.remove("hidden");
  opTimer = seconds;
  opShown = 0;
}
function updateOperator(dt) {
  if (opTimer <= 0) return;
  opTimer -= dt;
  opShown += dt;
  if (opQueue.length && opShown >= config.operator.minSeconds) showOperator(opQueue.shift());
  else if (opTimer <= 0) $("operator").classList.add("hidden");
}
function clearOperator() {
  opQueue.length = 0;
  opTimer = 0;
  $("operator").classList.add("hidden");
  $("hint").classList.add("hidden");
}
// 遊び方の説明(ミラとは別の枠)
function hint(text) {
  replayAnim($("hint"), text);
}

// ---- 画面の切り替え ----
const screens = ["title", "tutorial", "levelup", "pause", "dead", "replay", "result", "hangar", "stages", "mission-clear"];
function show(name) {
  for (const s of screens) $(s).classList.toggle("hidden", s !== name);
  if (mode !== "chart" && mode !== "levelup") $("chart").classList.add("hidden"); // 強化の状態のウィンドウは、ほかの画面へ移ったら閉じる
  $("hud").classList.toggle("hidden", !["play", "tutorial", "levelup", "pause", "chart", "dead", "finale"].includes(mode));
  $("hud").classList.toggle("levelup", mode === "levelup"); // 強化選びの間は、下のヒントを隠す(画面の説明と重なる)
  $("hud").classList.toggle("paused", mode === "pause"); // ポーズ中は右上の視点ボタンを隠す(メニューに同じ選択がある)
  if (["replay", "title", "hangar", "result", "stages", "mission-clear"].includes(mode)) clearOperator();
}
function setMode(m, screen = null) {
  mode = m;
  show(screen);
}

function isMobile() {
  return window.matchMedia("(pointer: coarse)").matches;
}

// 面を選んで出撃(n: 1〜6。src/sim/missions.js)
let missionNo = 1;
function startRun(n = missionNo) {
  missionNo = n;
  save = loadSave();
  applyCameraLock();
  const seed = (Date.now() ^ (Math.random() * 0x7fffffff)) | 0;
  const bonus = bonusesFor(save);
  world = createWorld({ seed, maxAlive: isMobile() ? config.enemies.maxAlive.mobile : config.enemies.maxAlive.pc, meta: bonus.meta, mission: n });
  stats?.reset();
  // 格納庫で選んだ「最初から持って出る武器」(記録の前に入れるので、リプレイも同じになる)
  if (save.startWeapon && WEAPONS[save.startWeapon]) {
    world.player.weapons[save.startWeapon] = 1;
    world.player.cooldowns[save.startWeapon] = 0;
  }
  // 確認用の ?skip・?weapons で遊んだ回は、ずっと続く記録に残さない(結果の画面には出す)
  const params = new URLSearchParams(location.search);
  finale = null;
  lastBigKill = null;
  run = { maxCombo: 0, bossKilled: false, rewinds: 0, settled: null, debug: params.has("skip") || params.has("weapons") };
  if (!run.debug) {
    save.records.runs++;
    writeSave(save);
  }
  // 確認用: ?weapons=spread:3,saber:2 で、最初から持つ武器とレベルを決める(記録の前に入れるので、リプレイも同じになる)
  for (const pair of (new URLSearchParams(location.search).get("weapons") || "").split(",").filter(Boolean)) {
    const [id, lv] = pair.split(":");
    if (WEAPONS[id]) {
      world.player.weapons[id] = Math.min(WEAPONS[id].levels.length + 1, Math.max(1, Number(lv) || 1));
      world.player.cooldowns[id] = 0;
    }
  }
  rec = createRecorder(world);
  stock = config.rewind.stock + bonus.extraRewind;
  // 確認用: ?skip=秒 で、その時間まで自動で進めてから始める(その間は無敵。強化は毎回いちばん左を選ぶ)
  const skip = Number(new URLSearchParams(location.search).get("skip")) || 0;
  while (world.progress < skip && !world.over) {
    const inp = { mx: Math.round(Math.sin(world.tick / 60) * 100), my: 0 };
    if (world.pendingLevelUp) inp.choice = 0;
    world.player.invuln = 1;
    rec.advance(world, inp);
  }
  pendingChoice = null;
  pendingRescue = null;
  timeCalls.clear();
  acc = 0;
  resetJuice();
  view.clear();
  view.resetStage();
  clearOperator();
  setMode("play");
  sound.play("start", { volume: 0.7 });
  sound.startBgm();
  // 初めての出撃(この端末)は、試合を止めたままミラの説明から。確認用の ?skip・?weapons の回は出さない
  if (!run.debug && !getDevice("tutorialSeen")) openTutorial("play");
  else greet();
}
function greet() {
  say(t("mila.start"));
  hint(t("hint.start"));
}

// ---- ミラの説明(初めての出撃の前。ポーズの「遊び方」からも開ける) ----
// 開いている間は mode が "tutorial" なので試合は進まない。閉じると back の画面へ戻る
let tutorial = null; // { page, back }
const tutPages = [...document.querySelectorAll("#tutorial .tut-page")];
function openTutorial(back) {
  tutorial = { page: 0, back };
  setMode("tutorial", "tutorial");
  showTutorialPage();
}
function showTutorialPage() {
  const last = tutPages.length - 1;
  tutPages.forEach((el, i) => el.classList.toggle("hidden", i !== tutorial.page));
  $("tut-say").textContent = t(`tutorial.say${tutorial.page + 1}`);
  $("tut-dots").textContent = tutPages.map((_, i) => (i === tutorial.page ? "●" : "○")).join(" ");
  $("tut-next").textContent = t(tutorial.page < last ? "tutorial.next" : tutorial.back === "play" ? "tutorial.launch" : "tutorial.close");
}
function nextTutorialPage() {
  if (!tutorial) return;
  if (tutorial.page < tutPages.length - 1) {
    tutorial.page++;
    showTutorialPage();
    return;
  }
  setDevice("tutorialSeen", true);
  const back = tutorial.back;
  tutorial = null;
  if (back === "play") {
    acc = 0;
    setMode("play");
    greet();
  } else setMode("pause", "pause");
}
$("tut-next").addEventListener("click", nextTutorialPage);
$("pause-howto-btn").addEventListener("click", () => {
  if (mode === "pause") openTutorial("pause");
});

// ---- 試合を進める ----
function stepLive(dt) {
  acc = Math.min(acc + dt, TICK * 5);
  while (acc >= TICK) {
    acc -= TICK;
    const inp = input.sample();
    if (pendingChoice != null) {
      inp.choice = pendingChoice;
      pendingChoice = null;
    }
    if (pendingRescue) inp.rewind = pendingRescue;
    const before = world.progress;
    if (!rec.advance(world, inp)) break;
    pendingRescue = null;
    handleEvents(world.events);
    announceNewEnemies(before, world.progress);
    announceTimeLeft(before, world.progress);
    if (world.pendingLevelUp) {
      openLevelUp();
      break;
    }
    if (world.over) {
      onOver();
      break;
    }
  }
}

function showBanner(small, big) {
  $("stage-no").textContent = small;
  $("stage-name").textContent = big;
  replayAnim($("stage-banner"), null);
}

view.onStageChange((stage) => {
  if (mode !== "play" && mode !== "levelup") return;
  showBanner(`AREA ${mission().stages.indexOf(stage) + 1}`, stage.name);
  say(t(`stage.${stage.id}.mila`));
  if (has(`stage.${stage.id}.hint`)) hint(t(`stage.${stage.id}.hint`));
  sound.play("boss", { volume: 0.5, rate: 1.2 });
});

let firstLevelUp = true;
let lastKillMilestone = 0;
// アニメーションを最初から流し直して表示する
function replayAnim(el, text) {
  if (text != null) el.textContent = text;
  el.classList.add("hidden");
  void el.offsetWidth;
  el.classList.remove("hidden");
}

function handleEvents(events) {
  view.onEvents(events, world);
  sound.onEvents(events);
  for (const e of events) {
    if (e.k === "hurt") hitStop = Math.max(hitStop, config.juice.hitStopHurt);
    if (e.k === "kill" && !e.crash) countCombo(e);
    if (e.k === "kill" && e.type === "boss") run.bossKilled = true;
    if (e.k === "kill" && e.big) lastBigKill = { ...e, time: world.time };
    if (e.k === "heal" && e.amount > 0) {
      replayAnim($("heal-pop"), `SHIELD +${e.amount}`);
      const bar = document.querySelector(".bar.shield");
      bar.classList.add("healing");
      setTimeout(() => bar.classList.remove("healing"), 900);
      if (e.source === "repair") say(t("mila.repaired"), "default", 2.5);
    }
    if (e.k === "holdStart") {
      showBanner("WARNING", t("banner.approach"));
      const h = mission().holds[e.index];
      say(has(`hold.${h.id}.mila`) ? t(`hold.${h.id}.mila`) : t("mila.holdDefault"));
      if (has(`hold.${h.id}.hint`)) hint(t(`hold.${h.id}.hint`));
      sound.play("boss", { volume: 0.7 });
    }
    if (e.k === "holdClear" && e.boss && !e.cleared && world.enemies.some((en) => en.leader && en.leave)) {
      // 母艦戦の時間切れ: 母艦が引き上げる(3〜6面。倒さなくても先へ進むことをはっきり見せる。2026-11-03 オーナー決定)
      showBanner("TIME UP", t("banner.bossEscape"));
      say(t("mila.bossEscape"));
      sound.play("slowEnd", { volume: 0.7, rate: 1.2 });
    }
    // 面の最後の停止戦(final)を抜けたらクリア: 「前進再開」や修理カプセルの案内は出さない(この後は大爆発とクリアの画面)
    if (e.k === "holdClear" && !e.boss && !world.finaleDone) {
      showBanner(e.cleared ? "ALL CLEAR" : "BREAK THROUGH", t("banner.advance"));
      if (e.cleared) {
        say(t("mila.holdClear"), "clear");
        hint(t("hint.holdClear"));
      }
      sound.play("slowEnd", { volume: 0.7, rate: 1.2 });
    }
    if (e.k === "chosen") {
      const d = describeChoice(e.choice);
      replayAnim($("toast"), t(d.level > 1 && !d.evolve ? "choice.toastLv" : "choice.toast", d));
    }
    if (e.k === "cannonCharge" && !run.cannonSeen) {
      // 大物のチャージ主砲: その回で初めてのときだけ、ミラが知らせる
      run.cannonSeen = true;
      say(t("mila.cannon"));
      hint(t("hint.cannon"));
    }
    if (e.k === "boss") {
      say(t("mila.boss"));
      hint(t("hint.boss"));
    } else if (e.k === "kill" && e.big) say(t(has(`mila.kill.${e.type}`) ? `mila.kill.${e.type}` : "mila.kill.other"), "clear");
  }
  const k = Math.floor(world.stats.kills / 100);
  if (k > lastKillMilestone) {
    lastKillMilestone = k;
    if (k === 1) say(t("mila.kills100"));
  }
}

// 新しい色の玉が出始めたら、ミラの一言と説明(config.enemies.roster。前進の秒で決まる)
function announceNewEnemies(before, now) {
  for (const it of mission().roster) {
    if (before < it.from && now >= it.from) {
      say(t(`enemy.${it.type}.mila`));
      hint(t(`enemy.${it.type}.hint`));
    }
  }
}

// 上の時計が 0 になる前進の秒 = 面の最後の大物(倒すまで終わらない停止戦)が出る秒(2026-11-03 オーナー決定)。
// 最後の大物を倒すとその場でクリアなので、面の長さ(runSeconds)まで数えると秒が余ったままクリアしていた
function finaleAt(m = mission()) {
  if (m.bossFinal && m.bossAt != null) return m.bossAt;
  const last = m.holds.findLast((h) => h.final);
  return last ? last.at : m.runSeconds;
}

// 残り時間が 1分ちょうどを切るたびに、ミラが「残りn分！」(2026-11-01 オーナー要望)。
// 残り = 最後の大物が出る秒(finaleAt) − 前進の秒(画面の上の時計と同じ。停止戦の間は減らない)。
// R.W.N.D. で戻って同じ分をまた通っても、言うのは1試合に1回だけ(timeCalls は startRun で空にする)
function announceTimeLeft(before, now) {
  const run = finaleAt();
  for (let m = Math.floor((run - before) / 60); m >= 1; m--) {
    const at = run - m * 60; // 残り m分 になる前進の秒
    if (before < at && now >= at && !timeCalls.has(m)) {
      timeCalls.add(m);
      say(t("mila.timeLeft", { n: m }));
      const clock = $("time");
      clock.classList.remove("time-call");
      void clock.offsetWidth;
      clock.classList.add("time-call"); // 時計も一緒に光らせる
    }
  }
}

function resetJuice() {
  hitStop = 0;
  lastStopAt = -1;
  combo.n = 0;
  combo.lastAt = -99;
}

function countCombo(e) {
  const J = config.juice;
  combo.n = world.time - combo.lastAt <= J.comboSeconds ? combo.n + 1 : 1;
  combo.lastAt = world.time;
  run.maxCombo = Math.max(run.maxCombo, combo.n);
  if (e.type !== "pod" && e.type !== "dasher" && world.time - lastStopAt > J.hitStopGap) {
    hitStop = Math.max(hitStop, J.hitStopKill);
    lastStopAt = world.time;
  }
  if (combo.n >= J.comboMin && combo.n % 10 === 0) {
    const el = $("combo-n");
    if (el.animate) el.animate([{ transform: "scale(1.5)" }, { transform: "scale(1)" }], { duration: 220, easing: "ease-out" });
  }
}

function openLevelUp() {
  setMode("levelup", "levelup");
  if (firstLevelUp) {
    say(t("mila.levelUp"));
    firstLevelUp = false;
    $("levelup-advice").classList.remove("hidden");
  } else $("levelup-advice").classList.add("hidden");
  // カードの下のチャート: 横に広い画面は押せるチャート、狭い画面は文字なしの小さいチャート(押すとウィンドウ)。出し分けは style.css
  $("levelup-radar").innerHTML = radarSvg(world.player, world.pendingLevelUp.choices);
  $("levelup-chart-btn").innerHTML = radarSvg(world.player, world.pendingLevelUp.choices, { mini: true });
  const box = $("choices");
  box.innerHTML = "";
  world.pendingLevelUp.choices.forEach((c, i) => {
    const d = describeChoice(c);
    const b = document.createElement("button");
    b.className = "choice";
    b.innerHTML = `<div class="icon">${choiceIcon(c)}</div><div class="name"></div><div class="lvl"></div><div class="desc"></div><div class="key">${i + 1}</div>`;
    b.querySelector(".name").textContent = d.name;
    b.querySelector(".lvl").textContent = d.evolve ? t("choice.evoTag") : d.level ? `Lv ${d.level}` : "";
    b.classList.toggle("evolve", !!d.evolve);
    b.querySelector(".desc").textContent = d.desc;
    b.addEventListener("click", () => choose(i));
    box.appendChild(b);
  });
}

function choose(i) {
  if (mode !== "levelup") return;
  pendingChoice = i;
  setMode("play");
}

function onOver() {
  if (world.over === "dead") {
    setMode("dead", "dead");
    $("dead-rewind-btn").disabled = stock <= 0;
    $("dead-rewind-btn").textContent = t("dead.rewind", { n: stock });
    say(t(stock > 0 ? "mila.dead" : "mila.deadNoStock"), "default", 6);
    if (stock > 0) hint(t("hint.dead"));
  } else {
    startFinale();
  }
}

// クリアした: すぐにクリアの画面を出さず、倒した大物の大爆発を見せてから(config.juice.finale)。試合はもう進めない
function startFinale() {
  const F = config.juice.finale;
  const boss = lastBigKill && world.time >= lastBigKill.time && world.time - lastBigKill.time <= F.bossRecentSeconds ? lastBigKill : null;
  const plan = planFinale(world, boss);
  finale = { t: 0, seconds: plan.seconds, events: plan.events, gone: new Set() };
  setMode("finale");
}

// 大爆発を1フレーム進める。終わったらクリアの画面へ。返す物: 描く試合(弾と爆発した敵を外した写し)
function stepFinale(dt) {
  finale.t += dt;
  const due = [];
  while (finale.events.length && finale.events[0].at <= finale.t) due.push(finale.events.shift());
  if (due.length) {
    for (const e of due) if (e.id != null) finale.gone.add(e.id);
    view.onEvents(due, world);
    sound.onEvents(due);
  }
  const shown = finaleView(world, finale.gone);
  if (finale.t >= finale.seconds) {
    finale = null;
    showMissionClear();
  }
  return shown;
}

// 面をクリア: ミラの絵を大きく出して一言(config.missions の clear)。タップで結果へ
function showMissionClear() {
  sound.stopBgm();
  const m = mission();
  const c = m.clear;
  setMode("mission-clear", "mission-clear");
  $("mc-stage").textContent = `STAGE ${m.n} CLEAR`;
  $("mc-img").src = `assets/textures/${c.img}.webp`;
  $("mc-text").textContent = t(`mission.${m.n}.clear`);
  $("mc-thanks").classList.toggle("hidden", !c.thanks);
  $("mc-link").classList.toggle("hidden", !c.link);
  sound.play("start", { volume: 0.7 });
}

function showResult() {
  sound.stopBgm();
  setMode("result", "result");
  const clear = world.over === "clear";
  $("result-title").textContent = `STAGE ${mission().n} ${clear ? "CLEAR" : "FAILED"}`;
  $("result-title").classList.toggle("danger", !clear);
  $("result-face").src = `assets/textures/mila-${clear ? "clear" : "recap"}.webp`;
  $("result-text").textContent = t(clear ? "result.clearText" : "result.failText");
  const s = world.stats;
  // 精算は1回だけ(リプレイを閉じて結果へ戻ったときは、前の中身をもう一度出す)
  if (!run.settled) {
    if (run.debug) save = structuredClone(save);
    run.settled = settleRun(save, { progress: world.progress, score: s.score, kills: s.kills, level: world.player.level, combo: run.maxCombo, clear, bossKilled: run.bossKilled, rewinds: run.rewinds, holdsFailed: s.holdsFailed || 0, mission: missionNo, dopaMul: mission().dopa, areas: mission().stages.filter((a) => a.from <= world.progress).map((a) => a.id) });
    if (!run.debug) writeSave(save);
  }
  const best = run.settled.before;
  const vsBest = (now, b, f = String) => (b > 0 ? (now > b ? t("result.bestNew") : t("result.bestWas", { v: f(b) })) : "");
  renderSettlement($("result-meta"), run.settled);
  $("result-stats").innerHTML = "";
  for (const line of [
    t("result.progress", { v: fmtTime(world.progress), best: vsBest(world.progress, best.bestProgress, fmtTime) }),
    t("result.kills", { v: s.kills, best: vsBest(s.kills, best.bestKills) }),
    t("result.comboLevel", { combo: run.maxCombo, level: world.player.level }),
    t("result.score", { v: s.score, best: vsBest(s.score, best.bestScore) }),
  ]) {
    const div = document.createElement("div");
    div.textContent = line;
    $("result-stats").appendChild(div);
  }
}

// ---- R.W.N.D. とリプレイ ----
function openReplay({ fromDeath = false, startTick = rec.endTick } = {}) {
  replay = { world: rec.worldAt(startTick), playing: false, speed: 1, fromDeath, acc: 0 };
  rescuePick = { heal: fromDeath === true, power: false }; // 撃墜から来たら、修理を最初から選んでおく(払えるときだけ。renderRescue)
  view.clear();
  setMode("replay", "replay");
  $("replay-head").textContent = fromDeath === null ? "REPLAY" : "R.W.N.D.";
  $("replay-camera").classList.toggle("hidden", fromDeath !== null); // 演出カメラは試合の後のリプレイだけ
  $("replay-photo").classList.toggle("hidden", fromDeath !== null); // 画面写真も試合の後のリプレイだけ
  const seek = $("replay-seek");
  seek.max = String(rec.endTick);
  seek.value = String(replay.world.tick);
  updateReplayUi();
}

function updateReplayUi() {
  const canRewind = stock > 0 && replay.fromDeath !== null && !(world.over === "clear");
  $("replay-rewind").disabled = !canRewind || replay.world.tick >= rec.endTick;
  $("replay-rewind").textContent = t("replay.rewindHere", { n: stock });
  $("replay-play").textContent = replay.playing ? "II" : "▶";
  $("replay-speed").textContent = `x${replay.speed}`;
  $("replay-time").textContent = fmtTime(replay.world.progress);
  $("replay-seek").value = String(replay.world.tick);
  renderRescue(canRewind);
}

// 救済の選択(払える DOPA の分だけ選べる)
function rescueCost(pick) {
  const B = config.rewind.boosts;
  return (pick.heal ? B.heal.cost : 0) + (pick.power ? B.power.cost : 0);
}
function renderRescue(show) {
  const box = $("rescue");
  box.classList.toggle("hidden", !show);
  if (!show) return;
  if (rescueCost(rescuePick) > save.dopa) rescuePick = { heal: false, power: false };
  const key = JSON.stringify([rescuePick, save.dopa, getLang()]);
  if (box.dataset.key === key) return;
  box.dataset.key = key;
  box.innerHTML = "";
  const head = document.createElement("div");
  head.className = "rescue-head";
  head.textContent = t("rescue.head", { dopa: save.dopa });
  box.appendChild(head);
  for (const [id, b] of Object.entries(config.rewind.boosts)) {
    const btn = document.createElement("button");
    btn.className = "rescue-pick";
    btn.classList.toggle("on", rescuePick[id]);
    btn.textContent = t("rescue.item", { name: t(`boost.${id}.name`), desc: t(`boost.${id}.desc`, b), cost: b.cost });
    const next = { ...rescuePick, [id]: !rescuePick[id] };
    btn.disabled = !rescuePick[id] && rescueCost(next) > save.dopa;
    btn.addEventListener("click", () => {
      rescuePick = next;
      renderRescue(true);
    });
    box.appendChild(btn);
  }
}

function seekReplay(tick) {
  replay.world = rec.worldAt(tick);
  view.clear();
  updateReplayUi();
}

function doRewind() {
  if (stock <= 0) return;
  stock--;
  run.rewinds++;
  // 救済の DOPA を払う(確認用の回は記録に残さない)
  const cost = rescueCost(rescuePick);
  const paid = cost <= save.dopa;
  if (paid && cost > 0) {
    save.dopa -= cost;
    if (!run.debug) writeSave(save);
  }
  pendingRescue = paid ? { ...rescuePick } : { heal: false, power: false };
  const t = replay.world.tick;
  rec.forkAt(t);
  world = replay.world;
  replay = null;
  resetJuice();
  view.clear();
  const fx = $("rewind-fx");
  fx.classList.remove("on");
  void fx.offsetWidth;
  fx.classList.add("on");
  sound.play("slowEnd", { volume: 0.7, rate: 0.7 });
  sound.startBgm();
  acc = 0;
  if (world.pendingLevelUp) openLevelUp();
  else setMode("play");
  const { heal, power } = pendingRescue;
  say(t(heal && power ? "mila.rewindBoth" : heal ? "mila.rewindHeal" : power ? "mila.rewindPower" : "mila.rewind"));
}

function stepReplay(dt) {
  if (!replay.playing) return;
  replay.acc = Math.min(replay.acc + dt * replay.speed, TICK * 10);
  while (replay.acc >= TICK) {
    replay.acc -= TICK;
    if (!rec.replayStep(replay.world)) {
      // 記録の最後のレベルアップの選択待ちなどで止まったら、再生も止める
      replay.playing = false;
      break;
    }
    view.onEvents(replay.world.events, replay.world);
    sound.onEvents(replay.world.events.filter((e) => e.k === "kill"));
  }
  updateReplayUi();
}

// ---- HUD ----
function fmtTime(sec) {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
// 上の時計(2026-11-03 オーナー決定「停止戦で時計が止まって、あと何秒か分かりづらい」):
// ふだんは最後の大物が出るまでの残り。停止戦の間は、その停止戦の時間切れまでの残り(色を変える)。
// 倒すまで終わらない戦い(最後の大物が残っている間)は DESTROY
function updateClock(w) {
  const clock = $("time");
  const h = w.hold;
  const leaderLeft = h && h.final && w.enemies.some((e) => e.leader && e.hp > 0);
  clock.classList.toggle("hold", !!h && !leaderLeft);
  clock.classList.toggle("destroy", !!leaderLeft);
  if (leaderLeft) clock.textContent = "DESTROY";
  else if (h) clock.textContent = String(Math.max(0, Math.ceil(config.holds.maxSeconds - h.elapsed)));
  else clock.textContent = fmtTime(finaleAt() - w.progress);
}
function updateHud(w) {
  const p = w.player;
  updateClock(w);
  const boostLeft = w.player.powerBoost || 0;
  $("boost-left").classList.toggle("hidden", !(boostLeft > 0));
  if (boostLeft > 0) $("boost-left").textContent = `POWER UP ${Math.ceil(boostLeft)}`;
  const holdLeft = w.hold && !w.hold.boss ? w.enemies.filter((e) => e.hold).length : 0;
  $("hold-left").classList.toggle("hidden", !holdLeft);
  if (holdLeft) $("hold-left").textContent = t("hud.holdLeft", { n: holdLeft });
  const shieldRatio = p.shield / maxShield(w);
  $("shield-fill").style.width = `${100 * shieldRatio}%`;
  $("ship-shield").classList.toggle("low", shieldRatio < config.graphics.shieldLowRatio);
  $("xp-fill").style.width = `${(100 * p.xp) / xpToNext(p.level)}%`;
  $("level").textContent = p.level;
  $("kills").textContent = w.stats.kills;
  $("stock").textContent = stock;
  // 大きな敵(母艦・小母艦・大玉)の体力のバー
  const boss = w.enemies.findLast((e) => config.enemies.types[e.type].big); // 2機いるときは新しく出た方
  $("boss-bar").classList.toggle("hidden", !boss);
  $("hold-left").classList.toggle("below-boss", !!boss); // 体力のバーと重ならないように下げる
  if (boss) {
    $("boss-label").textContent = config.enemies.types[boss.type].label;
    $("boss-fill").style.width = `${(100 * boss.hp) / boss.maxHp}%`;
  }
  const comboLeft = 1 - (w.time - combo.lastAt) / config.juice.comboSeconds;
  const showCombo = w === world && combo.n >= config.juice.comboMin && comboLeft > 0;
  $("combo").classList.toggle("hidden", !showCombo);
  if (showCombo) {
    $("combo-n").textContent = combo.n;
    $("combo-time").style.transform = `scaleX(${comboLeft})`;
  }
}

// ---- 強化の状態のウィンドウ(レーダーチャート。src/ui/radar.js) ----
// 2026-11-03 オーナー要望: 画面の下の武器の一覧をやめ、ボタンで開くチャートにした。
// プレイ中に開くと mode を "chart" にして時計を止める。レベルアップの画面からは、その上に重ねるだけ(もう止まっている)。
let chartBack = null; // 閉じたら戻る mode
function openChart() {
  if (mode !== "play" && mode !== "levelup") return;
  chartBack = mode;
  if (mode === "play") setMode("chart");
  const picking = mode === "levelup" && !!world.pendingLevelUp;
  $("chart-svg").innerHTML = radarSvg(world.player, picking ? world.pendingLevelUp.choices : []);
  $("chart-pick-hint").classList.toggle("hidden", !picking);
  // 攻撃力: プレイ中の出力強化 × 格納庫の出力 × 出力ブースト(sim の stepWeapons と同じ掛け算)。武器のLvとは別に効いていることを見せる
  $("chart-power").textContent = t("hud.power", { p: Math.round((attackPower(world) - 1) * 100) });
  $("chart").classList.remove("hidden");
}
function closeChart() {
  if ($("chart").classList.contains("hidden")) return;
  $("chart").classList.add("hidden");
  if (chartBack === "play" && mode === "chart") setMode("play");
  chartBack = null;
}
const chartOpen = () => !$("chart").classList.contains("hidden");
$("chart-btn").innerHTML = RADAR_ICON;
for (const id of ["chart-btn", "levelup-chart-btn"]) $(id).addEventListener("click", openChart);
$("chart-close").addEventListener("click", closeChart);
// レベルアップの画面から開いたとき: 光っている軸の名前を押すと、そのカードを選んだことにする
$("chart-svg").addEventListener("click", (e) => {
  const pick = e.target.closest && e.target.closest("[data-choice]");
  if (!pick || mode !== "levelup") return;
  closeChart();
  choose(Number(pick.dataset.choice));
});
// カードの下のチャート(横に広い画面)のオレンジの名前を押しても選ぶ
$("levelup-radar").addEventListener("click", (e) => {
  const pick = e.target.closest && e.target.closest("[data-choice]");
  if (pick && mode === "levelup") choose(Number(pick.dataset.choice));
});
$("chart").addEventListener("click", (e) => {
  if (e.target === $("chart")) closeChart(); // ウィンドウの外をタップしても閉じる
});

// ---- ボタン ----
$("start-btn").addEventListener("click", async () => {
  await sound.unlock();
  openStages();
});
// 面を選ぶカードの画面(クリアするたびにカードが増える。src/meta/screens.js)
function openStages() {
  sound.stopBgm();
  save = loadSave();
  renderStageCards($("stage-cards"), save, unlockedMissions(save, MISSION_COUNT), missionInfo, (n) => startRun(n));
  setMode("stages", "stages");
}
$("stages-back-btn").addEventListener("click", showTitle);
$("result-stages-btn").addEventListener("click", openStages);
$("mc-next").addEventListener("click", showResult);
$("mc-link").href = config.links.senseofvoid;
$("pause-btn").addEventListener("click", () => togglePause());
$("resume-btn").addEventListener("click", () => togglePause());
$("pause-rewind-btn").addEventListener("click", () => {
  openReplay({ fromDeath: false, startTick: Math.max(0, rec.endTick - config.rewind.backSecondsOnDeath * config.sim.tickRate) });
});
$("quit-btn").addEventListener("click", () => {
  sound.stopBgm();
  showTitle();
});
$("quality-select").addEventListener("change", (e) => view.quality.setLevel(e.target.value));
$("mute-btn").addEventListener("click", () => {
  sound.setMuted(!sound.muted);
  $("mute-btn").textContent = t(sound.muted ? "sound.off" : "sound.on");
});
// 視点の切り替え(背後・斜め見下ろし・主観。src/view/camera-modes.js)。選んだ視点はこの端末に覚えておく(src/meta/device.js)。
// 2026-10-31 オーナー要望「背後視点が一番わかりやすいので、背後デフォ、二回目以降に視点変更できるように」:
// 1回目のプレイは背後(order[0])に固定し、切り替えのボタンを出さない。結果まで遊んだ回数(save.records.runs)が config.graphics.cameraUnlockRuns 以上で切り替えられる
const cameraNames = config.graphics.cameras;
const cameraLabel = (m) => t(`camera.${m}`);
const cameraUnlocked = () => save.records.runs >= config.graphics.cameraUnlockRuns;
function setCamera(m, remember = true) {
  view.setCameraMode(m);
  const now = view.cameraMode;
  $("camera-btn").textContent = cameraLabel(now);
  $("camera-select").value = now;
  if (remember) setDevice("camera", now);
}
function nextCamera() {
  if (!cameraUnlocked()) return;
  const order = cameraNames.order;
  setCamera(order[(order.indexOf(view.cameraMode) + 1) % order.length]);
}
// 出撃のたびに呼ぶ。切り替えられないうちは背後に戻し、ボタンを隠す
function applyCameraLock() {
  const open = cameraUnlocked();
  $("camera-btn").classList.toggle("hidden", !open);
  for (const el of document.querySelectorAll(".camera-row")) el.classList.toggle("hidden", !open);
  if (open) setCamera(getDevice("camera") || cameraNames.order[0]);
  else setCamera(cameraNames.order[0], false);
}
for (const m of cameraNames.order) $("camera-select").add(new Option(cameraLabel(m), m));
applyCameraLock();
$("camera-btn").addEventListener("click", nextCamera);

// 試合の後のリプレイの演出カメラ(AUTO・俯瞰・真横など。src/view/replay-camera.js)。遊ぶときの視点とは別に、この端末に覚えておく
let replayCamera = REPLAY_CAMERA_MODES.includes(getDevice("replayCamera")) ? getDevice("replayCamera") : REPLAY_CAMERA_MODES[0];
function showReplayCamera() {
  $("replay-camera").textContent = `視点: ${replayCameraName(replayCamera)}`;
}
function nextReplayCamera() {
  replayCamera = REPLAY_CAMERA_MODES[(REPLAY_CAMERA_MODES.indexOf(replayCamera) + 1) % REPLAY_CAMERA_MODES.length];
  setDevice("replayCamera", replayCamera);
  showReplayCamera();
}
showReplayCamera();
$("replay-camera").addEventListener("click", nextReplayCamera);
// 画面写真(src/ui/snapshot.js): いま映っている所を1枚に。押した瞬間を写すため、再生中でもそのまま次の1枚を受け取る
let photoBusy = false;
$("replay-photo").addEventListener("click", async () => {
  if (!replay || photoBusy) return;
  photoBusy = true;
  const btn = $("replay-photo");
  try {
    const layers = await view.snapshot();
    const shot = replay.world;
    const canvas = composeSnapshot(layers, { stage: mission().n, progress: shot.progress, url: config.links.senseofdopa });
    const done = await deliverSnapshot(canvas, { name: snapshotName(mission().n, shot.progress), text: t("replay.photoText", { n: mission().n }), url: config.links.senseofdopa });
    if (done !== "cancelled") btn.textContent = t(done === "shared" ? "replay.photoShared" : "replay.photoSaved");
  } catch {
    btn.textContent = t("replay.photoFailed");
  }
  setTimeout(() => {
    btn.textContent = t("replay.photo");
    photoBusy = false;
  }, 1500);
});
$("camera-select").addEventListener("change", (e) => setCamera(e.target.value));

$("dead-rewind-btn").addEventListener("click", () => {
  openReplay({ fromDeath: true, startTick: Math.max(0, rec.endTick - config.rewind.backSecondsOnDeath * config.sim.tickRate) });
});
$("dead-result-btn").addEventListener("click", showResult);
$("result-retry-btn").addEventListener("click", () => startRun());
// 格納庫(戻るとタイトルへ)
function openHangar() {
  sound.stopBgm();
  save = loadSave();
  const draw = () => renderHangar($("hangar-box"), save, draw);
  draw();
  setMode("hangar", "hangar");
}
function showTitle() {
  save = loadSave();
  const r = save.records;
  const meta = $("title-meta");
  meta.textContent = "";
  if (r.runs) {
    const dopa = document.createElement("b");
    dopa.textContent = save.dopa;
    meta.append(dopa, " ", t("title.meta", { progress: fmtTime(r.bestProgress), kills: r.bestKills }));
  }
  setMode("title", "title");
}
$("hangar-btn").addEventListener("click", openHangar);
$("result-hangar-btn").addEventListener("click", openHangar);
$("hangar-back-btn").addEventListener("click", showTitle);
$("result-replay-btn").addEventListener("click", () => {
  openReplay({ fromDeath: null, startTick: 0 });
  replay.playing = true;
  updateReplayUi();
});
$("replay-play").addEventListener("click", () => {
  replay.playing = !replay.playing;
  updateReplayUi();
});
$("replay-speed").addEventListener("click", () => {
  replay.speed = replay.speed === 1 ? 2 : replay.speed === 2 ? 4 : 1;
  updateReplayUi();
});
$("replay-seek").addEventListener("input", (e) => {
  replay.playing = false;
  seekReplay(Number(e.target.value));
});
$("replay-rewind").addEventListener("click", doRewind);
$("replay-close").addEventListener("click", () => {
  const from = replay.fromDeath;
  replay = null;
  view.clear();
  if (from === true) setMode("dead", "dead");
  else if (from === false) setMode("pause", "pause");
  else showResult();
});

function togglePause() {
  if (mode === "play") {
    setMode("pause", "pause");
    $("pause-rewind-btn").disabled = stock <= 0;
    $("pause-rewind-btn").textContent = t("pause.rewind", { n: stock });
  } else if (mode === "pause") setMode("play");
}

window.addEventListener("keydown", (e) => {
  if (chartOpen()) {
    if (["Escape", "KeyP", "KeyC", "Enter", "Space"].includes(e.code)) closeChart();
    return;
  }
  if (e.code === "KeyC") return openChart();
  if (e.code === "Escape" || e.code === "KeyP") togglePause();
  if (e.code === "KeyV" && mode === "play") nextCamera();
  if (e.code === "KeyV" && mode === "replay") {
    if (replay && replay.fromDeath === null) nextReplayCamera();
    else nextCamera();
  }
  if (mode === "levelup" && ["Digit1", "Digit2", "Digit3"].includes(e.code)) choose(Number(e.code.slice(-1)) - 1);
  if (mode === "title" && (e.code === "Enter" || e.code === "Space")) $("start-btn").click();
  // 「次へ」にフォーカスがあるときはボタンが自分で押されるので、二重に進めない
  else if (mode === "tutorial" && (e.code === "Enter" || e.code === "Space") && document.activeElement !== $("tut-next")) nextTutorialPage();
});
document.addEventListener("visibilitychange", () => {
  if (document.hidden && mode === "play") togglePause();
  // 隠れている間は音も止める(描画はブラウザが止める)。戻ったら操作があったものとして数え直す
  if (document.hidden) sound.hold("hidden");
  else {
    sound.release("hidden");
    if (power.state !== "sleep") power.poke(performance.now());
  }
});

// ---- スマホの横持ち(2026-11-01 オーナー要望で禁止) ----
// 幕(#rotate)の出し入れは style.css の @media。ここでは試合中に横にされたらポーズする(戻しても勝手に再開しない)。
// 条件は style.css の #rotate の @media と同じにする
const PHONE_LANDSCAPE = window.matchMedia("(pointer: coarse) and (orientation: landscape) and (max-height: 500px)");
PHONE_LANDSCAPE.addEventListener("change", (e) => {
  if (e.matches && mode === "play") togglePause();
});

// ---- 省電力(src/power-save.js。秒数は config.powerSave) ----
// 見ているだけの画面で操作が無いと、描画を間引き → 止めて「タップで再開」を出す。試合中とリプレイの再生中は対象外
const power = createPowerSave(config.powerSave, performance.now());
const keepAwake = () => mode === "play" || mode === "finale" || (mode === "replay" && !!replay && replay.playing);
function sleepNow() {
  $("sleep").classList.remove("hidden");
  sound.hold("idle");
}
function wakeUp(e) {
  sound.release("idle");
  last = performance.now();
  requestAnimationFrame(frame);
  const hide = () => $("sleep").classList.add("hidden");
  if (e.type !== "pointerdown") return hide();
  // 起こしたタップの click が下のボタンを押さないよう、その click は捨ててから幕を外す
  // (click が来ないとき(指をずらした等)のために、指が離れて少ししたら外す)
  const done = () => {
    window.removeEventListener("click", eat, true);
    hide();
  };
  const eat = (ev) => {
    ev.stopImmediatePropagation();
    ev.preventDefault();
    done();
  };
  window.addEventListener("click", eat, { capture: true, once: true });
  const later = () => setTimeout(done, 500);
  window.addEventListener("pointerup", later, { once: true });
  window.addEventListener("pointercancel", later, { once: true });
}
// どの操作より先に受け取る(window の capture)。眠っていたら、起こすだけでその操作は使わない
for (const type of ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"]) {
  window.addEventListener(
    type,
    (e) => {
      if (!power.poke(performance.now())) return;
      if (type !== "pointermove" && type !== "touchstart") {
        e.stopImmediatePropagation();
        if (e.cancelable && type !== "pointerdown") e.preventDefault();
      }
      wakeUp(e);
    },
    { capture: true, passive: false },
  );
}

// ---- 確認用の負荷の表示(?stats。src/ui/stats.js)。fps・計算と描画の時間・敵の数・描画の細かさを左上に出す。試合の記録には影響しない ----
const stats = new URLSearchParams(location.search).has("stats") ? createStats($("stats")) : null;
if (stats) $("stats").classList.remove("hidden");

// ---- 毎フレーム ----
let last = performance.now();
function frame(now) {
  // 省電力: 眠ったら次のフレームを頼まない(起こすときに wakeUp が頼み直す)。間引き中は描かないフレームを飛ばす
  if (power.update(now, keepAwake()) === "sleep") return sleepNow();
  if (!power.shouldDraw(now)) return requestAnimationFrame(frame);
  const elapsed = now - last;
  const dt = Math.min(0.1, elapsed / 1000);
  last = now;
  let shown = world;
  const simStart = stats ? performance.now() : 0;
  let drawStart = simStart;
  if (mode === "title" || mode === "hangar" || mode === "stages") {
    // タイトルの後ろ: 見本の試合を自動で動かす(記録しない)。いつもは1フレーム1ステップ。間引き中は飛ばした分も進めて、同じ速さに見せる
    const steps = power.state === "low" ? Math.max(1, Math.min(4, Math.round((elapsed / 1000) * config.sim.tickRate))) : 1;
    for (let i = 0; i < steps; i++) demoStep();
    shown = demo;
  } else if (mode === "play") {
    // ヒットストップの間は試合を進めない(記録は毎ステップの入力なので、リプレイには影響しない)
    if (hitStop > 0) hitStop -= dt;
    else stepLive(dt);
  } else if (mode === "finale") {
    shown = stepFinale(dt);
  } else if (mode === "replay") {
    stepReplay(dt);
    shown = replay ? replay.world : world;
  }
  if (shown) {
    // 大爆発の間は、試合の SLOW(大物を倒したときのゆっくり)が止まったまま残るので、粒はふつうの速さで動かす
    let timeScale = mode === "finale" ? 1 : shown.slowTicks > 0 && mode !== "pause" && mode !== "chart" ? config.slow.scale : mode === "pause" || mode === "chart" || mode === "levelup" || mode === "tutorial" ? 0 : 1;
    if (mode === "play" && hitStop > 0) timeScale *= 0.05;
    if (stats) drawStart = performance.now();
    view.render(shown, dt, timeScale, mode === "replay" && replay && replay.fromDeath === null ? replayCamera : null);
    if (mode !== "title") updateHud(shown);
  }
  if (stats) stats.sample({ dt: elapsed / 1000, sim: drawStart - simStart, draw: performance.now() - drawStart, world: shown, view: view.stats(), power: power.state, mode });
  updateOperator(dt);
  requestAnimationFrame(frame);
}

function demoStep() {
  const p = demo.player;
  const inp = { mx: Math.round(Math.sin(demo.tick / 70) * 70 - p.x), my: Math.round(Math.cos(demo.tick / 53) * 50 - p.y) };
  if (demo.pendingLevelUp) inp.choice = demo.tick % 3;
  step(demo, inp);
  view.onEvents(demo.events, demo);
  if (demo.over) demo = createWorld({ seed: (Math.random() * 1e9) | 0 });
}

// ---- 言語(src/i18n/。ブラウザの言語で自動。ポーズの「言語」とタイトルのボタンで切り替え、この端末に覚える) ----
// 言語が変わったら、決まった文(index.html の data-i18n)に加えて、コードで入れている文を入れ直す。
// 切り替えられるのはタイトルとポーズの画面だけ。出ている途中のミラの一言などは、次の一言から新しい言語になる
function refreshTexts() {
  $("lang-select").value = getLangChoice();
  $("mute-btn").textContent = t(sound.muted ? "sound.off" : "sound.on");
  $("camera-btn").textContent = cameraLabel(view.cameraMode);
  for (const o of $("camera-select").options) o.textContent = cameraLabel(o.value);
  if (mode === "title") showTitle();
  if (mode === "pause") $("pause-rewind-btn").textContent = t("pause.rewind", { n: stock });
}
$("lang-select").addEventListener("change", (e) => setLang(e.target.value));
$("lang-btn").addEventListener("click", () => setLang(getLang() === "ja" ? "en" : "ja"));
onLangChange(refreshTexts);
applyDom();
refreshTexts();

showTitle();
requestAnimationFrame(frame);
